import type { Persona } from '@personawiz/shared';

/**
 * Generate a concise markdown summary of a persona suitable for
 * pasting into system prompts, docs, or other tools.
 */
export function personaToMarkdown(persona: Persona): string {
  const { coreIdentity, cognitiveProfile, uxBlueprint, promptDNA } = persona;

  return `# ${coreIdentity.personaName}

**Role:** ${coreIdentity.userRole}
**Use Case:** ${coreIdentity.useCase}

> ${coreIdentity.description}

## Core Identity

- **North Star Metric:** ${coreIdentity.northStarMetric}
- **Primary Goals:** ${coreIdentity.primaryGoals.join('; ')}
- **Pain Points:** ${coreIdentity.painPoints.join('; ')}

## Cognitive Profile

| Attribute | Value |
|-----------|-------|
| Thinking Style | ${cognitiveProfile.thinkingStyle} |
| Processing Mode | ${cognitiveProfile.processingMode} |
| Decision Style | ${cognitiveProfile.decisionStyle} |
| Risk Tolerance | ${cognitiveProfile.riskTolerance}% |
| Attention Span | ${cognitiveProfile.attentionSpan} |

## UX Blueprint

| Attribute | Value |
|-----------|-------|
| Information Density | ${uxBlueprint.informationDensity}/10 |
| Friction Threshold | ${uxBlueprint.frictionThreshold}/10 |
| Navigation Style | ${uxBlueprint.navigationStyle} |
| Visual Complexity | ${uxBlueprint.visualComplexity} |
| Interaction Pace | ${uxBlueprint.interactionPace} |
| Guidance Level | ${uxBlueprint.guidanceLevel} |

## Prompt DNA

| Attribute | Value |
|-----------|-------|
| Tone | ${promptDNA.tone} |
| Verbosity | ${promptDNA.verbosity}/10 |
| Explanation Depth | ${promptDNA.explanationDepth} |
| Example Frequency | ${promptDNA.exampleFrequency} |
| Jargon Level | ${promptDNA.jargonLevel} |
| Structure Preference | ${promptDNA.structurePreference} |
| Code Preference | ${promptDNA.codePreference} |
| Uncertainty Expression | ${promptDNA.uncertaintyExpression} |
`;
}

/**
 * Generate a system-prompt-ready snippet that another AI can consume directly.
 */
export function personaToSystemPrompt(persona: Persona): string {
  const { coreIdentity, cognitiveProfile, promptDNA } = persona;

  return `You are interacting with a "${coreIdentity.userRole}" persona whose primary use case is: "${coreIdentity.useCase}".

Persona: ${coreIdentity.personaName}
${coreIdentity.description}

North Star Metric: ${coreIdentity.northStarMetric}

Communication Guidelines (Prompt DNA):
- Tone: ${promptDNA.tone}
- Verbosity: ${promptDNA.verbosity}/10
- Explanation Depth: ${promptDNA.explanationDepth}
- Examples: ${promptDNA.exampleFrequency}
- Jargon Level: ${promptDNA.jargonLevel}
- Structure: ${promptDNA.structurePreference}
- Code Snippets: ${promptDNA.codePreference}
- Uncertainty: ${promptDNA.uncertaintyExpression}

Cognitive Profile:
- Thinking Style: ${cognitiveProfile.thinkingStyle}
- Processing Mode: ${cognitiveProfile.processingMode}
- Decision Making: ${cognitiveProfile.decisionStyle}
- Risk Tolerance: ${cognitiveProfile.riskTolerance}%
- Attention Span: ${cognitiveProfile.attentionSpan}

Adapt all responses to match these persona preferences.`;
}

/**
 * Copy text to clipboard, returns true on success.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Download persona as a JSON file.
 */
export function downloadPersonaJSON(persona: Persona): void {
  const blob = new Blob([JSON.stringify(persona, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${persona.coreIdentity.personaName.replace(/\s+/g, '-').toLowerCase()}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
